import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { Observable } from "rxjs/Observable";
import {
  filter,
  finalize,
  retry,
  shareReplay,
  switchMap,
  take,
} from "rxjs/operators";
import { Subject } from "rxjs/Subject";
import * as Enums from "../../app/access-control/access-control-constant";
import { IamAsk } from "../../models/iam/iam-ask";
import { NavigationService } from "../../navigation/navigation.service";
import { ConfigurationService } from "../../providers/configuration/configuration.service";
import { IamDataService } from "../../providers/iam-service/iam.data.service";
import { AuthService } from "../auth/auth.service";
import { PermissionsResponse } from "./permissions-response";

@Injectable()
export class AccessControlService {
  dataUrl: string;
  apiUrl: string;
  data: PermissionsResponse;
  _isAccessDataIsLoaded: boolean;
  isAccessDataIsLoaded: Subject<any> = new Subject<any>();
  volunteerAccessdata = new BehaviorSubject({
    volunteer: false,
    voladmin: false,
  });
  private permissionCache = new Map<string, Observable<PermissionsResponse>>();
  constructor(
    public navSvc: NavigationService,
    public http: HttpClient,
    private configurationService: ConfigurationService,
    private authSvc: AuthService,
    private iamDataService: IamDataService
  ) {
    this.dataUrl = configurationService.getConfig().apiUrl;
    if (this.configurationService.getConfig().isLocal) {
      this.apiUrl =
        configurationService.getConfig().localEndpoints.users + "/iam";
    } else {
      this.apiUrl = configurationService.getConfig().apiUrl + "/iam";
    }
  }

  //USE ONLY THIS METHOD TO GET PERMISSIONS, STOP USING OTHERS
  getPermission(permissionsList: string[], pageName?: string) {
    return new Observable<PermissionsResponse>((observer) => {
      this.getAccessControlData(permissionsList).subscribe(
        (data) => {
          this.data = data;
          if (pageName) {
            var hasAccess = this.data.permissions.filter(
              (permission) => permission.name == pageName
            );
            if (hasAccess.length > 0) {
              if (!hasAccess[0].access) {
                setTimeout(() => {
                  this.navSvc.goToInternalPage("AccessDeniedPage", {});
                }, 0);
                return false;
              }
            }
          }

          observer.next(data);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  // NOVA-2024: permission asks issued before the Cognito session is hydrated go
  // out header-less and get rejected at the gateway, silently hiding every
  // fail-closed gate. This variant holds the ask until authentication is
  // established; the retry resubscribes the whole chain, so it also re-waits
  // for auth if the session dropped in between.
  getPermissionWhenAuthenticated(
    permissionsList: string[]
  ): Observable<PermissionsResponse> {
    return this.authSvc.authenticationSubject.pipe(
      filter((isAuthenticated) => isAuthenticated === true),
      take(1),
      switchMap(() => this.getPermission(permissionsList)),
      retry(1)
    );
  }

  setIsAccessDataIsLoaded(value: any) {
    this._isAccessDataIsLoaded = value;
    if (this._isAccessDataIsLoaded) {
      this.isAccessDataIsLoaded.next(this._isAccessDataIsLoaded);
    }
  }

  //DON'T USE THIS METHOD
  //USE getPermission METHOD INSTEAD
  getAccessControlData(permissions: string[]): Observable<PermissionsResponse> {
    const cacheKey = [...permissions].sort().join(",");
    if (this.permissionCache.has(cacheKey)) {
      return this.permissionCache.get(cacheKey);
    }

    const result$ = new Observable<PermissionsResponse>((observer) => {
      if (this.authSvc.getUser() && this.authSvc.getUser().tenantId) {
        const ask: IamAsk = {
          locationId: this.authSvc.getUser().tenantId.toString(),
          permissions: permissions,
        };

        this.iamDataService.hasAccess(ask).subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            observer.error(err);
            observer.complete();
          }
        );
      } else {
        this.authSvc.signOut();
        setTimeout(() => {
          observer.error("User must be authenticated.");
          observer.complete();
        }, 0);
      }
    }).pipe(
      finalize(() => this.permissionCache.delete(cacheKey)),
      shareReplay(1)
    );

    this.permissionCache.set(cacheKey, result$);
    return result$;
  }

  //DON'T USE THIS METHOD ANYMORE
  //USE getPermission METHOD INSTEAD
  getResourcesData(pageName: string[]) {
    this.getAccessControlData(pageName).subscribe(
      (data) => {
        this.data = data;
      },
      (err) => {
        console.error(err);
      }
    );
  }

  //DON'T USE THIS METHOD ANYMORE
  //USE getPermission METHOD INSTEAD
  canAccessPage(nav: NavigationService, pageName: string[]) {
    this.getAccessControlData(pageName).subscribe(
      (data) => {
        this.data = data;
        var hasAccess = this.data.permissions.filter(
          (permission) => permission.name == pageName[0]
        );

        if (hasAccess.length > 0) {
          if (!hasAccess[0].access) {
            setTimeout(() => {
              nav.goToInternalPage("AccessDeniedPage", {});
            }, 0);
            return false;
          }
          return hasAccess[0].access;
        }
      },
      (err) => {
        console.error(err);
      }
    );
  }

  ////DON'T USE THIS METHOD ANYMORE
  //USE getPermission METHOD INSTEAD
  pcCanAccessPage(pageName: string[]) {
    return this.canAccessPage(this.navSvc, pageName);
  }

  getAccessLevel(permissionsResponse: PermissionsResponse, permission: string) {
    var hasPermission = permissionsResponse.permissions
      ? permissionsResponse.permissions.filter(
          (perm) => perm.name == permission
        )
      : [];
    if (hasPermission.length > 0) {
      if (hasPermission[0].access) {
        var permissionsSplitted = hasPermission[0].name.split(":");
        let level = permissionsSplitted.pop();

        if (
          level == "WRITE" ||
          level == "*" ||
          level == "EDIT" ||
          level == "ADD" ||
          level == "DELETE"
        ) {
          return Enums.ACCESS_LEVEL.WRITE;
        } else if (level == "VIEW") {
          return Enums.ACCESS_LEVEL.READ;
        } else {
          return Enums.ACCESS_LEVEL.NONE;
        }
      }
    }

    return Enums.ACCESS_LEVEL.NONE;
  }

  //DON'T USE THIS METHOD ANYMORE
  //USE getPermission METHOD INSTEAD
  setVolunteerAccessData(data) {
    this.volunteerAccessdata.next(data);
  }

  //DON'T USE THIS METHOD ANYMORE
  //USE getPermission METHOD INSTEAD
  getVolunteerAccessData() {
    return this.volunteerAccessdata.asObservable();
  }

  hasAccess(
    permissionsAccess: PermissionsResponse,
    permissionName: string
  ): boolean {
    if (!permissionsAccess || !permissionsAccess.permissions) return false;

    let searchPermission = permissionsAccess.permissions.find(
      (x) => x.name === permissionName
    );
    return searchPermission && searchPermission.access;
  }
}



// WEBPACK FOOTER //
// ./src/app/access-control/access-control-service.ts